import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import SettingsIcon from '@mui/icons-material/Settings';
import { JsonInput } from 'react-admin-json-view';
import { useTranslate } from 'ra-core';
import { useFormContext } from 'react-hook-form';
import {
  CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY,
  ClientConfigKeyOverrideFormEntry,
  formatOsFilterChipLabel,
} from '../../lib/clientConfigHelpers';
import {
  ClientConfigSchemaFieldDefinition,
  ConfigFieldByType,
} from '../../lib/clientConfigSchemaField';

const CLIENT_CONFIG_KEY_OVERRIDE_DRAFT_FORM_KEY = '_clientConfigKeyOverrideDraft';
const OVERRIDE_VALUE_SOURCE = `${CLIENT_CONFIG_KEY_OVERRIDE_DRAFT_FORM_KEY}.overrideValue`;

const overrideIdentity = (entry: ClientConfigKeyOverrideFormEntry) => (
  `${entry.osType}::${(entry.osVersionRange || '').trim()}`
);

const OverrideValueInputBySchema = (props: {
  schema?: Record<string, unknown>;
}) => {

  const { schema } = props;
  if (!schema) {

    return (
      <JsonInput
        source={OVERRIDE_VALUE_SOURCE}
        reactJsonOptions={{ name: OVERRIDE_VALUE_SOURCE, collapsed: false, displayDataTypes: false }}
      />
    );

  }
  const definition: ClientConfigSchemaFieldDefinition = {
    key: OVERRIDE_VALUE_SOURCE,
    schema,
    mergeHistory: [],
  };
  return (
    <ConfigFieldByType
      definition={definition}
      fallback={(
        <JsonInput
          source={OVERRIDE_VALUE_SOURCE}
          reactJsonOptions={{ name: OVERRIDE_VALUE_SOURCE, collapsed: false, displayDataTypes: false }}
        />
      )}
    />
  );

};

export const ClientConfigKeyOverridesControl = (props: {
  configKey: string;
  schema?: Record<string, unknown>;
}) => {

  const { configKey, schema } = props;
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const overridesRecord = watch(CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY) || {};
  const overrides = (overridesRecord[configKey] || []) as ClientConfigKeyOverrideFormEntry[];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<Pick<ClientConfigKeyOverrideFormEntry, 'osType' | 'osVersionRange'>>({
    osType: 'ios',
    osVersionRange: '',
  });
  const [duplicateError, setDuplicateError] = useState(false);

  const chipLabels = useMemo(() => ({
    allDevices: translate('clientConfig.osFilter.allDevices', { _: 'All devices' }),
    ios: translate('clientConfig.osFilter.ios', { _: 'iOS' }),
    android: translate('clientConfig.osFilter.android', { _: 'Android' }),
  }), [translate]);

  const setOverrides = (nextOverrides: ClientConfigKeyOverrideFormEntry[]) => {

    setValue(CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY, {
      ...overridesRecord,
      [configKey]: nextOverrides,
    }, { shouldDirty: true });

  };

  const openDialog = (index: number | null, initialValue: unknown) => {

    setEditingIndex(index);
    setDraft(index === null ? { osType: 'ios', osVersionRange: '' } : {
      osType: overrides[index].osType,
      osVersionRange: overrides[index].osVersionRange || '',
    });
    setValue(OVERRIDE_VALUE_SOURCE, initialValue, { shouldDirty: false });
    setDuplicateError(false);
    setDialogOpen(true);

  };

  const closeDialog = () => {

    setDialogOpen(false);
    setEditingIndex(null);
    setDuplicateError(false);

  };

  const applyDraft = () => {

    const identity = overrideIdentity({
      ...draft,
      value: watch(OVERRIDE_VALUE_SOURCE),
    });
    const duplicate = overrides.some((entry, index) => (
      index !== editingIndex && overrideIdentity(entry) === identity
    ));
    if (duplicate) {

      setDuplicateError(true);
      return;

    }

    const existingEntry = editingIndex !== null ? overrides[editingIndex] : undefined;
    const entry: ClientConfigKeyOverrideFormEntry = {
      ...draft,
      value: watch(OVERRIDE_VALUE_SOURCE),
      ...(existingEntry?.editableBy ? { editableBy: existingEntry.editableBy } : {}),
    };
    const nextOverrides = [...overrides];
    if (editingIndex === null) {

      nextOverrides.push(entry);

    } else {

      nextOverrides[editingIndex] = entry;

    }
    setOverrides(nextOverrides);
    closeDialog();

  };

  const removeOverride = () => {

    if (editingIndex === null) {

      return;

    }
    setOverrides(overrides.filter((_entry, entryIndex) => entryIndex !== editingIndex));
    closeDialog();

  };

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
    }}>
      {overrides.map((entry, index) => (
        <Chip
          key={`${configKey}-${overrideIdentity(entry)}-${index}`}
          size="small"
          label={formatOsFilterChipLabel({
            type: 'osFilter',
            osType: entry.osType,
            osVersionRange: entry.osVersionRange,
          }, chipLabels)}
          onClick={() => openDialog(index, entry.value)}
          sx={{ height: 24, cursor: 'pointer' }}
        />
      ))}
      <IconButton
        size="small"
        onClick={() => openDialog(null, _.cloneDeep(watch(configKey)))}
        aria-label={translate('clientConfig.osFilter.addOverride', { _: 'Add OS override' })}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {translate('clientConfig.osFilter.overridesTitle', { _: 'OS-specific override' })}
        </DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 1,
        }}>
          <FormControl size="small" fullWidth sx={{ marginTop: '10px' }}>
            <InputLabel id={`${configKey}-override-os-type`}>
              {translate('clientConfig.osFilter.osType', { _: 'Operating system' })}
            </InputLabel>
            <Select
              labelId={`${configKey}-override-os-type`}
              label={translate('clientConfig.osFilter.osType', { _: 'Operating system' })}
              value={draft.osType}
              onChange={(event) => setDraft((previous) => ({
                ...previous,
                osType: event.target.value as 'ios' | 'android',
              }))}
            >
              <MenuItem value="ios">{chipLabels.ios}</MenuItem>
              <MenuItem value="android">{chipLabels.android}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label={translate('clientConfig.osFilter.osVersionRange', { _: 'OS version range' })}
            helperText={translate('clientConfig.osFilter.osVersionRangeHelp', { _: 'Optional semver, e.g. >=12.0.0' })}
            value={draft.osVersionRange || ''}
            onChange={(event) => setDraft((previous) => ({
              ...previous,
              osVersionRange: event.target.value,
            }))}
          />
          <OverrideValueInputBySchema schema={schema} />
          {duplicateError && (
            <Typography variant="caption" color="error">
              {translate('clientConfig.osFilter.duplicateOsType', { _: 'An override for this OS and version range already exists.' })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          {editingIndex !== null ? (
            <Button color="error" onClick={removeOverride}>
              {translate('ra.action.delete', { _: 'Delete' })}
            </Button>
          ) : (
            <span />
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={closeDialog}>
              {translate('ra.action.cancel', { _: 'Cancel' })}
            </Button>
            <Button variant="contained" onClick={applyDraft}>
              {translate('ra.action.confirm', { _: 'Confirm' })}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );

};
